import React, { useState, useRef, useEffect } from "react";
import JisCheckbox from "./jisCheckbox"; // your existing checkbox
import DynamicIcon from "../dynamicicon";

export default function MultiSelectDropdown({
  options,
  placeholder = "Select options",
  onChange,
  value, // Add value prop to make it controlled (optional - undefined means uncontrolled)
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const dropdownRef = useRef();
  const isControlled = value !== undefined; // Check if component is controlled

  // Sync internal state with external value prop (only for controlled mode)
  useEffect(() => {
    if (!isControlled) return; // Skip for uncontrolled mode

    if (value && Array.isArray(value) && value.length > 0) {
      // Map value to full option objects if needed
      const selectedOptions = value
        .map((v) => {
          // If value items already have both id and title in correct format, use as-is
          if (v.id && v.title) return v;

          // Check if it has causeId (from selectedCauses format)
          if (v.causeId && v.description) {
            // Return the value with standardized format, preserving description even if options aren't loaded yet
            return { id: v.causeId, title: v.description };
          }

          // Check if it has causeId but no description (try to find in options)
          if (v.causeId) {
            const foundOption = options.find((opt) => opt.id === v.causeId);
            if (foundOption) {
              return { id: foundOption.id, title: foundOption.title };
            }
            // If option not found but we have causeId, still include it (options might load later)
            return { id: v.causeId, title: v.description || "Loading..." };
          }

          // Otherwise find the matching option from the options list
          if (v.id) {
            return options.find((opt) => opt.id === v.id) || null;
          }

          return null;
        })
        .filter(Boolean); // Remove any undefined/null values

      // Only update if the selection has actually changed
      setSelected((prev) => {
        const prevIds = prev.map((p) => p.id).sort().join(',');
        const newIds = selectedOptions.map((s) => s.id).sort().join(',');
        return prevIds === newIds ? prev : selectedOptions;
      });
    } else {
      setSelected((prev) => prev.length === 0 ? prev : []);
    }
  }, [value, options, isControlled]);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleSelect = (option, checked) => {
    let updated;
    if (checked) {
      updated = [...selected, option];
    } else {
      updated = selected.filter((s) => s.id !== option.id);
    }

    // Update internal state immediately for responsiveness
    setSelected(updated);

    // Call onChange with the updated selection
    onChange?.(updated);
  };

  const isSelected = (id) => selected.some((s) => s.id === id);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={dropdownRef}
      className="multi-select-dropdown"
      style={{ position: "relative", width: "300px" }}
    >
      {/* Dropdown Header */}
      <div
        className="dropdown-header"
        onClick={toggleDropdown}
        tabIndex={0}
        style={{
          border: "1px solid #ccc",
          borderRadius: "6px",
          padding: "8px 12px",

          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "nowrap",
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleDropdown();
          }
        }}
      >
        <span>
          {selected.length > 0 ? (
            selected.map((s) => s.title).join(", ")
          ) : (
            <span style={{ color: "#aaa" }}>{placeholder}</span>
          )}
        </span>
        <span
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0)",
            transition: "0.2s",
          }}
        >
          <DynamicIcon iname={"chevron-down"} />
        </span>
      </div>

      {/* Dropdown List */}
      {isOpen && (
        <div
          className="dropdown-list"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: "6px",
            marginTop: "4px",
            maxHeight: "250px",
            overflowY: "auto",
            zIndex: 1000,
            padding: "6px 8px",
          }}
        >
          {options.map((opt) => (
            <JisCheckbox
              key={opt.id}
              label={opt.title}
              value={isSelected(opt.id)}
              onChange={(checked) => handleSelect(opt, checked)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
